import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Music, ExternalLink, Filter, X } from "lucide-react";

const MOODS = ["all", "angry", "sad", "happy", "reminder", "recovery", "reflection"];
const MOOD_LABEL = {
  angry: "Angry",
  sad: "Sad",
  happy: "Happy",
  reminder: "A reminder",
  recovery: "Recovery-focused",
  reflection: "Reflection",
};
const MOOD_HINT = {
  angry: "For when the volume is the point.",
  sad: "For when you just need company.",
  happy: "For when you want to remember the light.",
  reminder: "Songs tied to specific moments — the kind that ambush you.",
  recovery: "The core of the story.",
  reflection: "For the quieter middle.",
};
const MOOD_COLOR = {
  angry: "text-red-300 border-red-500/40 bg-red-500/5",
  sad: "text-blue-300 border-blue-500/40 bg-blue-500/5",
  happy: "text-yellow-200 border-yellow-500/40 bg-yellow-500/5",
  reminder: "text-purple-300 border-purple-500/40 bg-purple-500/5",
  recovery: "text-teal-300 border-teal-500/40 bg-teal-500/5",
  reflection: "text-neutral-200 border-neutral-600 bg-neutral-800/40",
};

const ONBOARD_KEY = "wwr_soundtrack_onboard_v1";

const SoundtrackViewer = () => {
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [locked, setLocked] = useState(false);
  const [filter, setFilter] = useState("all");
  const [showOnboard, setShowOnboard] = useState(false);

  useEffect(() => {
    api.get("/soundtrack")
      .then(({ data }) => {
        setData(data);
        // Show mood onboarding on first arrival with at least one track
        if ((data.tracks || []).length > 0 && !localStorage.getItem(ONBOARD_KEY)) {
          setShowOnboard(true);
        }
      })
      .catch((e) => {
        if (e?.response?.status === 403) setLocked(true);
        else toast.error("Could not load the soundtrack");
      });
  }, []);

  const purchase = async () => {
    if (!user) { nav("/signup"); return; }
    try {
      const { data } = await api.post("/payments/checkout", {
        toolkit_id: "the_soundtrack",
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
    }
  };

  const pickMood = (mood) => {
    setFilter(mood);
    localStorage.setItem(ONBOARD_KEY, "1");
    setShowOnboard(false);
  };
  const skipOnboard = () => {
    localStorage.setItem(ONBOARD_KEY, "1");
    setShowOnboard(false);
  };

  const availableMoods = useMemo(() => {
    if (!data?.tracks) return new Set();
    return new Set(data.tracks.map((t) => t.mood).filter(Boolean));
  }, [data]);

  if (locked) {
    return (
      <div className="fade-in max-w-2xl mx-auto px-6 py-24 text-center" data-testid="soundtrack-locked">
        <Music className="mx-auto text-teal-400 mb-4" size={40} />
        <h1 className="font-display text-4xl mb-3">Locked</h1>
        <p className="text-neutral-400 mb-8">The Soundtrack is a paid companion. Unlock it for $14.99.</p>
        <button onClick={purchase} data-testid="soundtrack-unlock-btn" className="btn-primary px-6 py-3">
          Unlock for $14.99
        </button>
      </div>
    );
  }

  if (!data) return <div className="p-12 text-center text-teal-200/70">Loading…</div>;

  const filtered = filter === "all" ? data.tracks : data.tracks.filter((t) => t.mood === filter);

  return (
    <div className="fade-in max-w-4xl mx-auto px-6 py-16" data-testid="soundtrack-view">
      {/* Mood onboarding overlay */}
      {showOnboard && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/60 backdrop-blur-sm fade-in"
          data-testid="mood-onboarding"
        >
          <div className="relative max-w-2xl w-full card-surface p-10">
            <button
              onClick={skipOnboard}
              data-testid="mood-onboarding-skip"
              aria-label="Skip"
              className="absolute top-4 right-4 text-neutral-500 hover:text-neutral-200"
            >
              <X size={18} />
            </button>
            <div className="text-xs uppercase tracking-[0.24em] text-teal-300/80 mb-3">Welcome</div>
            <h2 className="font-display text-3xl mb-3">How are you arriving today?</h2>
            <p className="text-neutral-400 mb-8">
              Pick a mood and I'll drop you straight into that slice of the playlist. You can always change it.
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
              {["angry", "sad", "happy", "reminder", "recovery", "reflection"].map((m) => (
                <button
                  key={m}
                  onClick={() => pickMood(m)}
                  disabled={!availableMoods.has(m)}
                  data-testid={`mood-pick-${m}`}
                  className={`text-left rounded-xl border p-4 transition-all ${
                    availableMoods.has(m)
                      ? `${MOOD_COLOR[m]} hover:scale-[1.02] cursor-pointer`
                      : "border-neutral-800 bg-neutral-900/40 text-neutral-600 cursor-not-allowed opacity-60"
                  }`}
                >
                  <div className="font-display text-lg mb-1">{MOOD_LABEL[m]}</div>
                  <div className="text-xs opacity-80">{MOOD_HINT[m]}</div>
                  {!availableMoods.has(m) && (
                    <div className="text-[10px] uppercase tracking-widest mt-2 opacity-60">Not tagged yet</div>
                  )}
                </button>
              ))}
            </div>
            <div className="mt-6 text-center">
              <button
                onClick={() => pickMood("all")}
                data-testid="mood-onboarding-all"
                className="text-sm text-teal-300 hover:text-teal-200"
              >
                Show me everything →
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="text-xs uppercase tracking-[0.24em] text-teal-300/80 mb-4">The Soundtrack</div>
      <h1 className="font-display text-5xl mb-3">{data.title}</h1>
      <p className="font-display text-xl text-teal-200/80 mb-6">{data.subtitle}</p>
      <p className="text-neutral-400 leading-relaxed mb-8 max-w-2xl">{data.description}</p>

      {data.disclaimer && (
        <div className="mb-8 rounded-2xl border border-amber-500/30 bg-amber-500/5 p-4 text-sm text-amber-100/90 leading-relaxed">
          {data.disclaimer}
        </div>
      )}

      {data.playlist_url && (
        <a
          href={data.playlist_url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="soundtrack-playlist-link"
          className="btn-primary px-6 py-3 inline-flex items-center gap-2 mb-10"
        >
          <Music size={16} /> Open playlist in YouTube Music <ExternalLink size={14} />
        </a>
      )}

      {data.tracks.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-neutral-500 mb-3">
            <Filter size={12} /> Filter by mood
          </div>
          <div className="flex flex-wrap gap-2" data-testid="soundtrack-filters">
            {MOODS.map((m) => (
              <button
                key={m}
                onClick={() => setFilter(m)}
                data-testid={`filter-${m}`}
                className={`text-xs uppercase tracking-widest px-3 py-1.5 rounded-full border transition-colors ${
                  filter === m
                    ? "bg-teal-500 text-black border-teal-500"
                    : "border-neutral-700 text-neutral-400 hover:border-teal-500/40 hover:text-teal-200"
                }`}
              >
                {m}
              </button>
            ))}
          </div>
        </div>
      )}

      {data.tracks.length === 0 ? (
        <div className="card-surface p-10 text-center text-neutral-400">
          The track-by-track guide is on its way — the playlist link above works today.
        </div>
      ) : (
        <ol className="space-y-2" data-testid="soundtrack-tracks">
          {filtered.map((t, i) => (
            <li key={t.id} className="card-surface p-5" data-testid={`track-${t.id}`}>
              <div className="flex items-start gap-4">
                <div className="text-xs text-neutral-500 font-mono w-8 flex-shrink-0 pt-1">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-4 flex-wrap">
                    <div>
                      <span className="font-display text-lg text-neutral-100">{t.title}</span>
                      <span className="text-neutral-500 text-sm"> — {t.artist}</span>
                    </div>
                    <span className={`text-xs uppercase tracking-widest border rounded-full px-2.5 py-0.5 ${MOOD_COLOR[t.mood] || MOOD_COLOR.recovery}`}>
                      {t.mood}
                    </span>
                  </div>
                  {t.note && (
                    <p className="text-sm text-neutral-300 leading-relaxed mt-2 italic">"{t.note}"</p>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ol>
      )}

      {filtered.length === 0 && data.tracks.length > 0 && (
        <div className="text-center text-neutral-500 mt-8 text-sm">Nothing in that mood yet.</div>
      )}
    </div>
  );
};

export default SoundtrackViewer;
